import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { adminApi } from '../services/api';
import FeatureSelector from './FeatureSelector';
import ReferralSourceSelect from './ReferralSourceSelect';
import AccountManagerSelect from './AccountManagerSelect';
import { IS_BROKER_ADMIN } from '../config/admin-build';

interface OrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const OrganizationDialog: React.FC<OrganizationDialogProps> = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    domain: '',
    maxRunsPerMonth: 50,
    sharedRunPool: true,
    accountManagerName: '',
    accountManagerEmail: '',
    referralSource: '',
  });
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = ['Basic Information', 'Features'];

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleDisplayNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = event.target.value;
    
    // Auto-generate organization name from display name (kebab-case)
    const autoName = displayName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    setFormData(prev => ({
      ...prev,
      displayName,
      name: autoName
    }));
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate basic info
    if (!formData.displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (!formData.name.trim()) {
      setError('Organization name is required');
      return;
    }

    // Validate email format if provided
    if (formData.accountManagerEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.accountManagerEmail.trim())) {
      setError('Invalid email format for account manager email');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await adminApi.createOrganization({
        name: formData.name.trim(),
        displayName: formData.displayName.trim(),
        domain: formData.domain.trim() || undefined,
        maxRunsPerMonth: formData.maxRunsPerMonth || 50,
        sharedRunPool: formData.sharedRunPool,
        features: selectedFeatures,
        accountManagerName: formData.accountManagerName.trim() || undefined,
        accountManagerEmail: formData.accountManagerEmail.trim() || undefined,
        referralSource: formData.referralSource.trim() || undefined,
      });

      // Reset form
      setFormData({
        name: '',
        displayName: '',
        domain: '',
        maxRunsPerMonth: 50,
        sharedRunPool: true,
        accountManagerName: '',
        accountManagerEmail: '',
        referralSource: '',
      });
      setSelectedFeatures([]);
      setActiveStep(0);

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Validate before moving to next step
    if (activeStep === 0) {
      if (!formData.displayName.trim()) {
        setError('Display name is required');
        return;
      }
      if (!formData.name.trim()) {
        setError('Organization name is required');
        return;
      }
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        displayName: '',
        domain: '',
        maxRunsPerMonth: 50,
        sharedRunPool: true,
        accountManagerName: '',
        accountManagerEmail: '',
        referralSource: '',
      });
      setSelectedFeatures([]);
      setActiveStep(0);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Create New Organization</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Display Name"
              value={formData.displayName}
              onChange={handleDisplayNameChange}
              required
              fullWidth
              autoFocus
              disabled={loading}
              helperText="Human-readable name for the organization"
            />

            <TextField
              label="Organization ID"
              value={formData.name}
              onChange={handleChange('name')}
              required
              fullWidth
              disabled={loading}
              helperText="Auto-generated from display name (e.g., 'My Company' → 'my-company')"
            />

            <TextField
              label="Domain"
              value={formData.domain}
              onChange={handleChange('domain')}
              fullWidth
              disabled={loading}
              placeholder="example.com"
              helperText="Optional: Email domain for auto-assignment"
            />

            <TextField
              label="Monthly Run Limit"
              type="number"
              value={formData.maxRunsPerMonth}
              // Parse to a NUMBER — the generic handleChange stores event.target.value
              // (a string), which the create-organization API then persisted raw,
              // producing string-typed maxRunsPerMonth rows. (Backend now coerces too.)
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, maxRunsPerMonth: parseInt(e.target.value, 10) || 50 }));
                setError(null);
              }}
              required
              fullWidth
              disabled={loading}
              InputProps={{
                endAdornment: <InputAdornment position="end">runs</InputAdornment>,
              }}
              inputProps={{ min: 1 }}
              helperText="Maximum number of jobs per month for users in this organization"
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sharedRunPool}
                    onChange={(e) => setFormData(prev => ({ ...prev, sharedRunPool: e.target.checked }))}
                    disabled={loading}
                  />
                }
                label="Shared Run Pool"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                When enabled, all members share the organization's monthly run limit.
                When disabled, each member uses their own individual allocation.
              </Typography>
            </Box>

            {/* Our staff member who looks after this client — picked from the
                Admins group, never typed. Free text here is what produced 25
                spellings of 9 people in prod. Distinct from the org's
                owner/payer, who is someone inside the client's organisation. */}
            <Box sx={{ pt: 2 }}>
              <AccountManagerSelect
                labelId="create-org-account-manager-label"
                valueEmail={formData.accountManagerEmail}
                valueName={formData.accountManagerName}
                onChange={(selection) =>
                  setFormData((prev) => ({
                    ...prev,
                    accountManagerName: selection?.name || '',
                    accountManagerEmail: selection?.email || '',
                  }))
                }
                disabled={loading}
                helperText="Optional: the internal account manager for this organisation. They receive its run-limit alerts."
              />
            </Box>

            {/* Referral source — broker-AI distribution-partner tracking,
                shown only on the broker-AI admin build. (Trello #260) */}
            {IS_BROKER_ADMIN && (
              <ReferralSourceSelect
                labelId="referral-source-label"
                value={formData.referralSource}
                onChange={(v) => setFormData((prev) => ({ ...prev, referralSource: v }))}
                disabled={loading}
                helperText="Optional: where this organisation was referred from (e.g. FSP Hub) — for referral & revenue-share tracking."
              />
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              General features are enabled by default. You can customize which features this organization has access to.
            </Alert>
            <FeatureSelector
              selectedFeatures={selectedFeatures}
              onChange={setSelectedFeatures}
              disabled={loading}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button 
            variant="contained" 
            onClick={handleNext} 
            disabled={loading}
          >
            Next
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Creating...' : 'Create Organization'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationDialog;
